import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type { ArStatementSendTaskCustomer } from '@erp/shared-types';

/**
 * Imperative resolver for "who still needs a payment reminder", for one
 * payment-reminder task.
 *
 * A payment-reminder task's action payload carries the customer list its scan
 * resolved, which goes stale as soon as anyone sends a statement by hand — the
 * task keeps asking for customers who no longer need one until the next
 * scheduled scan re-runs. Callers resolve the live set at the moment the user
 * opens the send flow instead.
 *
 * Takes only the task id: the reminder terms live on the task's action payload,
 * and the server reads them from there rather than trusting the client to echo
 * them back.
 *
 * Deliberately imperative (`fetchQuery` from a click handler) rather than an
 * always-mounted `useQuery`: the AR summary card renders one of these per task
 * row and must not fire a report-sized aging query for tasks nobody opens.
 * `staleTime: 0` because the entire point is freshness at open time.
 */
export function useResolveArPaymentReminderCustomers() {
  const queryClient = useQueryClient();

  return useCallback(
    async (taskId: string): Promise<ArStatementSendTaskCustomer[]> => {
      const data = await queryClient.fetchQuery(
        orpc.accountsReceivable.arPaymentReminders.resolveCustomers.queryOptions(
          { input: { taskId } },
        ),
      );
      return data.customers;
    },
    [queryClient],
  );
}
